export const TUMOR_SITE_IDS = {
  THYROID: 1,
  BREAST: 2,
  LUNG: 3,
};

export const TUMOR_SITE_FEATURES = {
  ENVIRONMENTAL_SETTINGS: "environmentalSettings",
  RISK_FACTORS: "riskFactors",
  NEOADJUVANT_TREATMENT: "neoadjuvantTreatment",
  SPECIMEN_TYPE: "specimenType",
  STAGE: "stage",
  CYTOLOGY: "cytology",
  SPECIFIC_COHORT: "specificCohort",
  LYMPHOVASCULAR_INVASION: "lymphovascularInvasion",
  BIOMARKER_PROFILE: "biomarkerProfile",
  DIAGNOSTIC_FEATURES_SIMPLE_FREE_TEXT: "diagnosticFeaturesSimpleFreeText",
  ALPHABETICAL_STAIN_STUDY_SORT: "alphabeticalStainStudySort",
};

export const TUMOR_SITE_FEATURES_BY_ID = {
  [TUMOR_SITE_IDS.THYROID]: [
    TUMOR_SITE_FEATURES.ENVIRONMENTAL_SETTINGS,
    TUMOR_SITE_FEATURES.STAGE,
    TUMOR_SITE_FEATURES.CYTOLOGY,
    TUMOR_SITE_FEATURES.SPECIFIC_COHORT,
  ],
  [TUMOR_SITE_IDS.BREAST]: [
    TUMOR_SITE_FEATURES.RISK_FACTORS,
    TUMOR_SITE_FEATURES.NEOADJUVANT_TREATMENT,
    TUMOR_SITE_FEATURES.SPECIMEN_TYPE,
    TUMOR_SITE_FEATURES.LYMPHOVASCULAR_INVASION,
    TUMOR_SITE_FEATURES.BIOMARKER_PROFILE,
    TUMOR_SITE_FEATURES.DIAGNOSTIC_FEATURES_SIMPLE_FREE_TEXT,
    TUMOR_SITE_FEATURES.ALPHABETICAL_STAIN_STUDY_SORT,
  ],
  [TUMOR_SITE_IDS.LUNG]: [
    TUMOR_SITE_FEATURES.RISK_FACTORS,
    TUMOR_SITE_FEATURES.STAGE,
  ],
};

export const TUMOR_SITE_FIELD_BY_FEATURE = {
  [TUMOR_SITE_FEATURES.ENVIRONMENTAL_SETTINGS]: "environmentalSettings",
  [TUMOR_SITE_FEATURES.RISK_FACTORS]: "riskFactors",
  [TUMOR_SITE_FEATURES.NEOADJUVANT_TREATMENT]: "neoadjuvantTreatment",
  [TUMOR_SITE_FEATURES.SPECIMEN_TYPE]: "specimenType",
  [TUMOR_SITE_FEATURES.STAGE]: "stage",
  [TUMOR_SITE_FEATURES.CYTOLOGY]: "cytology",
  [TUMOR_SITE_FEATURES.SPECIFIC_COHORT]: "specificCohort",
  [TUMOR_SITE_FEATURES.LYMPHOVASCULAR_INVASION]: "lymphovascularInvasion",
  [TUMOR_SITE_FEATURES.BIOMARKER_PROFILE]: "biomarkerProfile",
};

export const TUMOR_SITE_VALIDATION_FIELDS_BY_FEATURE = {
  [TUMOR_SITE_FEATURES.RISK_FACTORS]: ["riskFactors"],
  [TUMOR_SITE_FEATURES.ENVIRONMENTAL_SETTINGS]: ["environmentalSettings"],
  [TUMOR_SITE_FEATURES.STAGE]: ["Stage"],
  [TUMOR_SITE_FEATURES.CYTOLOGY]: ["Cytology"],
  [TUMOR_SITE_FEATURES.SPECIFIC_COHORT]: ["SpecificCohort"],
  [TUMOR_SITE_FEATURES.LYMPHOVASCULAR_INVASION]: ["lymphovascularInvasion"],
  [TUMOR_SITE_FEATURES.NEOADJUVANT_TREATMENT]: ["neoadjuvantTreatment"],
  [TUMOR_SITE_FEATURES.SPECIMEN_TYPE]: ["specimenType"],
  [TUMOR_SITE_FEATURES.BIOMARKER_PROFILE]: ["biomarkerProfile", "biomarkerProfiles"],
};


export const IMMUNOHISTO_CATEGORY_GROUPS_BY_TUMOR_SITE_ID = {
  [TUMOR_SITE_IDS.THYROID]: {
    1: { name: "IHC stains", order: 1 },
    2: { name: "HC stains", order: 2 },
  },
  [TUMOR_SITE_IDS.BREAST]: {
    3: { name: "IHC Breast Biomarkers", order: 1 },
    4: { name: "IHC Other Stains", order: 2 },
    5: { name: "Special Stains", order: 3 },
  },
  [TUMOR_SITE_IDS.LUNG]: {
    6: { name: "IHC stains", order: 1 },
  },
};

export const TUMOR_SITE_CASE_NUMBER_EXAMPLES_BY_ID = {
  [TUMOR_SITE_IDS.THYROID]: "TH-NA-E21-0001, TH-NA-E01-0002",
  [TUMOR_SITE_IDS.BREAST]: "BT-NA-E21-0001, BT-NA-E01-0002",
  [TUMOR_SITE_IDS.LUNG]: "LT-NA-E21-0001, LT-NA-E01-0002",
};

export const getTumorSiteId = (tumorSite) => {
  if (tumorSite && typeof tumorSite === "object") {
    return Number(tumorSite.id || tumorSite.tumorSiteId || tumorSite.tumor_site_id);
  }

  return Number(tumorSite);
};

export const hasTumorSiteFeature = (tumorSite, feature) => {
  const tumorSiteId = getTumorSiteId(tumorSite);
  const features = TUMOR_SITE_FEATURES_BY_ID[tumorSiteId] || [];

  return features.includes(feature);
};

export const hasAnyTumorSiteFeature = (tumorSite, features) =>
  features.some((feature) => hasTumorSiteFeature(tumorSite, feature));

export const isThyroidTumorSite = (tumorSite) =>
  getTumorSiteId(tumorSite) === TUMOR_SITE_IDS.THYROID;

export const isBreastTumorSite = (tumorSite) =>
  getTumorSiteId(tumorSite) === TUMOR_SITE_IDS.BREAST;

export const isLungTumorSite = (tumorSite) =>
  getTumorSiteId(tumorSite) === TUMOR_SITE_IDS.LUNG;

export const getUnsupportedTumorSiteFields = (tumorSite) =>
  Object.entries(TUMOR_SITE_FIELD_BY_FEATURE)
    .filter(([feature]) => !hasTumorSiteFeature(tumorSite, feature))
    .map(([, field]) => field);

export const getUnsupportedTumorSiteValidationFields = (tumorSite) =>
  Object.entries(TUMOR_SITE_VALIDATION_FIELDS_BY_FEATURE)
    .filter(([feature]) => !hasTumorSiteFeature(tumorSite, feature))
    .flatMap(([, fields]) => fields);

export const getImmunohistoCategoryGroup = (tumorSite, categoryId) => {
  const tumorSiteId = getTumorSiteId(tumorSite);
  const siteGroups = IMMUNOHISTO_CATEGORY_GROUPS_BY_TUMOR_SITE_ID[tumorSiteId] || {};

  return siteGroups[categoryId] || { name: "Others", order: 3 };
};

export const getTumorSiteCaseNumberPlaceholder = (tumorSite) =>
  TUMOR_SITE_CASE_NUMBER_EXAMPLES_BY_ID[getTumorSiteId(tumorSite)] ||
  "Enter comma-separated case IDs";
